import createHyphenator from 'hyphen';
import patternsEn from 'hyphen/patterns/en-us.js';
import patternsDe from 'hyphen/patterns/de-1996.js';

const hyphenatorEn = createHyphenator(patternsEn, { sync: true, hyphenChar: '\u00AD', html: true });
const hyphenatorDe = createHyphenator(patternsDe, { sync: true, hyphenChar: '\u00AD', html: true });

/**
 * Remove soft hyphens (\u00AD) from text.
 */
export const cleanHyphens = (text) => {
  if (!text || typeof text !== 'string') return text || '';
  return text.replace(/\u00AD/g, '');
};

/**
 * Hyphenate text (lowercase, Capitalized, or ALL-CAPS) using soft hyphens (\u00AD).
 * Browser will automatically break lines and render '-' only when a word overflows.
 */
export const hyphenateText = (text, langCode = 'de') => {
  if (!text || typeof text !== 'string') return text || '';

  const clean = text.replace(/\u00AD/g, '');
  if (!clean.trim()) return clean;

  const lang = (langCode || '').toLowerCase();
  const hyphenator = lang.startsWith('en') ? hyphenatorEn : hyphenatorDe;

  try {
    return hyphenator(clean);
  } catch (err) {
    return clean;
  }
};

/**
 * Save current caret character position within an element,
 * calculating the offset based on visible (clean) characters without \u00AD.
 */
export const saveCaretPosition = (context) => {
  if (typeof window === 'undefined' || !context) return null;
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return null;

  const range = selection.getRangeAt(0);
  if (!context.contains(range.commonAncestorContainer)) return null;

  const preRange = range.cloneRange();
  preRange.selectNodeContents(context);
  preRange.setEnd(range.endContainer, range.endOffset);
  const textBefore = preRange.toString().replace(/\u00AD/g, '');
  return textBefore.length;
};

/**
 * Restore caret position in element to the given visible character offset.
 */
export const restoreCaretPosition = (context, charOffset) => {
  if (
    typeof window === 'undefined' ||
    charOffset === null ||
    charOffset === undefined ||
    !context
  ) {
    return;
  }

  const selection = window.getSelection();
  if (!selection) return;

  let currentCount = 0;
  const walker = document.createTreeWalker(context, NodeFilter.SHOW_TEXT, null, false);
  let currentNode = walker.nextNode();
  let lastTextNode = null;

  while (currentNode) {
    lastTextNode = currentNode;
    const text = currentNode.nodeValue;
    const cleanLength = text.replace(/\u00AD/g, '').length;

    if (currentCount + cleanLength >= charOffset) {
      let cleanIndex = 0;
      const targetClean = charOffset - currentCount;
      let actualIndex = 0;

      while (actualIndex < text.length && cleanIndex < targetClean) {
        if (text[actualIndex] !== '\u00AD') {
          cleanIndex++;
        }
        actualIndex++;
      }

      while (actualIndex < text.length && text[actualIndex] === '\u00AD') {
        actualIndex++;
      }

      const range = document.createRange();
      range.setStart(currentNode, Math.min(actualIndex, text.length));
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }

    currentCount += cleanLength;
    currentNode = walker.nextNode();
  }

  if (lastTextNode) {
    const range = document.createRange();
    range.setStart(lastTextNode, lastTextNode.nodeValue.length);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
  }
};
